import cn from 'classnames';
import { serialize } from 'object-to-formdata';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Icon, Image } from 'components';
import CancelButton from 'components/CancelButton';
import { ButtonV3, DropdownV3, InputV2 } from 'components/ComponentV2';
import Tooltip from 'components/Tooltip';
import { Button, Form, Input, TextArea } from 'components/form';
import { Col, Row } from 'components/layout';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { useCommonList } from 'lib/contexts/common-list-context';
import { CompanyResponse } from 'lib/models/company';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { http } from 'lib/utils/http';
import { parseErrorResponse } from 'lib/utils/parser';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step4.module.scss';

interface Step4Props {
  progress: number;
  setStep: (step: number) => void;
}

interface CompanyAttributes {
  industry_id?: string;
  sub_industry_id?: string;
  employees_count?: string;
  logo?: string | FileList;
  short_description?: string;
  description?: string;
  custom_industry_name?: string;
}

export const Step4: React.FC<Step4Props> = ({ progress, setStep }) => {
  const { recruiterCompany, updateUserApiResponse, recruiter } = useAuth();
  const { industryOptions, employeesCountRange } = useCommonList();
  const [showSubIndustry, setShowSubIndustry] = React.useState<boolean>(
    industryOptions?.find(
      (industry) => industry.id === recruiterCompany?.sub_industries.data[0]?.id
    )?.value
      ? true
      : false
  );

  const [t] = useTranslation('signup.recruiter');
  const [showIndustryNameInput, setShowIndustryNameInput] = useState(
    recruiterCompany?.custom_industry_name ? true : false
  );

  const emptyFileList = new DataTransfer().files;
  const icon_url: string | FileList =
    recruiterCompany?.icon_url || emptyFileList;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    setError,
  } = useForm({
    defaultValues: {
      industry_id: industryOptions?.find(
        (industry) => industry.id === recruiterCompany?.industry.data?.id
      )?.value,
      sub_industry_id: industryOptions?.find(
        (industry) =>
          industry.id === recruiterCompany?.sub_industries.data[0]?.id
      )?.value,
      employees_count: recruiterCompany?.employees_count,
      logo: icon_url,
      short_description: recruiterCompany?.short_description,
      description: recruiterCompany?.description,
      custom_industry_name: recruiterCompany?.custom_industry_name,
    },
  });

  const [showError, setShowError] = React.useState<string>('');

  const onSubmit: SubmitHandler<CompanyAttributes> = async (data) => {
    const industry_id = industryOptions?.find(
      (industry) => industry.value === data.industry_id
    )?.id;
    const sub_industry_id = industryOptions?.find(
      (industry) => industry.value === data.sub_industry_id
    )?.id;
    const employees_count = employeesCountRange?.find(
      (count) => count.value === data.employees_count
    )?.id;

    const body = {
      company: {
        ...data,
        ...(typeof data.logo !== 'string' && {
          icon: data.logo && data.logo[0],
        }),
        industry_id,
        sub_industry_id,
        employees_count,
      },
    };

    const formData = serialize(body, { indices: true });

    if (!recruiter) return;

    const plainHeaders: HeadersInit = new Headers();
    plainHeaders.set('X-Auth-Email', recruiter.email);
    plainHeaders.set('X-Auth-Token', recruiter.authentication_token);

    try {
      await http<CompanyResponse>(API_ROUTES.COMPANY_PROFILE, {
        method: 'PUT',
        body: formData,
        headers: plainHeaders,
      });

      const recruiter = { flow_state: ONBOARDING_FLOW_STATE.PRE_5 };
      const { data: response } = await apiInstance.patch(
        API_ROUTES.RECRUITER_SIGNUP,
        {
          recruiter,
        }
      );
      updateUserApiResponse(response);
      setStep(5);
    } catch (error) {
      const message = parseErrorResponse(error);
      setShowError(message);
    }
  };

  register('industry_id', { required: t('step-4.industry.required') });
  register('sub_industry_id', {
    required: showSubIndustry ? t('step-4.sub-industry.required') : false,
  });
  register('employees_count', { required: t('step-4.company-size.required') });
  register('logo', { required: t('step-4.logo.required') });

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const files: FileList = target.files as FileList;
    setValue('logo', files, { shouldDirty: true });
    setError('logo', {});
  };

  const currentIndustryId = watch('industry_id');
  const currentSubIndustryId = watch('sub_industry_id');
  const currentEmployeesCount = watch('employees_count');
  const logo = watch('logo');

  const logoImage =
    typeof logo === 'object' && logo?.length > 0 && logo !== null
      ? URL.createObjectURL(logo[0])
      : typeof logo === 'object' && logo !== null
      ? typeof logo === 'object' && logo.length === 0
        ? ''
        : URL.createObjectURL(logo as unknown as Blob)
      : typeof logo === 'string'
      ? logo
      : '';

  const selectedIndustry =
    industryOptions?.find((industry) => industry?.value === currentIndustryId)
      ?.label || '';

  const selectedSubIndustry =
    industryOptions?.find(
      (industry) => industry?.value === currentSubIndustryId
    )?.label || '';

  const selectedEmployeesCount =
    employeesCountRange?.find((count) => count.value === currentEmployeesCount)
      ?.label || '';

  const handleSubIndustryDelete = () => {
    setValue('sub_industry_id', '');
    setShowSubIndustry(false);
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setStep(3);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-4.header.title')}
          desc={t('step-4.header.desc')}
        />

        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row
            className={cn(styles.mb24, {
              [styles.hideMargin]: !showIndustryNameInput,
            })}
          >
            {industryOptions && (
              <>
                <Col span={24} className={styles.companyIndustry}>
                  <DropdownV3
                    title={t('step-4.industry.title')}
                    label={t('step-4.industry.label')}
                    value={currentIndustryId || ''}
                    onChange={(value: string) => {
                      setValue('industry_id', value);
                      setError('industry_id', {});
                    }}
                    options={industryOptions}
                    isSearchable={true}
                    searchPlaceholder={t('search-placeholder')}
                    selectedValueLabel={selectedIndustry}
                    isValid={!errors.industry_id?.message}
                    labelClassName={styles.fieldLabel}
                    className={styles.CompanyDropDown}
                    triggerButtonClassName={styles.triggerButton}
                  />
                  {!showSubIndustry && (
                    <div className={styles.subIndustryButtonWrapper}>
                      <Tooltip
                        content={t('step-4.industry.tooltip')}
                        position="top"
                        xAlign="center"
                        color="dark"
                        className={styles.tooltipContainer}
                      >
                        <Button
                          variant="text"
                          color="black"
                          className={styles.subIndustryButton}
                          onClick={() => {
                            setShowSubIndustry(true);
                          }}
                        >
                          {t('step-4.sub-industry.add')}
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                  {errors.industry_id?.message && (
                    <div className={styles.errorMessage}>
                      {errors.industry_id?.message}
                    </div>
                  )}
                </Col>
              </>
            )}
          </Row>
          {showIndustryNameInput ? (
            <Row className={styles.mb24}>
              <Col span={24} className={styles.companyIndustry}>
                <Input
                  label={t('step-4.industry-name.label')}
                  {...register('custom_industry_name', {
                    maxLength: {
                      value: 36,
                      message: t('step-4.industry-name.size-error'),
                    },
                  })}
                  isValid={!errors.custom_industry_name}
                  labelClassName={styles.labelField}
                  inputClassName={styles.inputField}
                  placeholder={t('step-4.industry-name.placeholder')}
                  showOptionalLabel={true}
                  infoToolTip={
                    <Tooltip
                      content={t('step-4.industry-name.tooltip')}
                      position="top"
                      xAlign="center"
                      color="dark"
                      className={cn(styles.tooltipContainer)}
                    >
                      <Icon iconName="icon_info" size="xsmall" />
                    </Tooltip>
                  }
                />
                <CancelButton
                  onRemove={() => {
                    setValue('custom_industry_name', '');
                    setShowIndustryNameInput(false);
                  }}
                  isShow={true}
                  placeholderClassName={styles.closeIcon}
                />
                {errors.custom_industry_name?.message && (
                  <div className={styles.errorMessage}>
                    {errors.custom_industry_name?.message}
                  </div>
                )}
              </Col>
            </Row>
          ) : (
            <Tooltip
              content={t('step-4.industry-name.add.tooltip')}
              position="top"
              xAlign="center"
              color="dark"
              className={cn(styles.tooltipContainer)}
            >
              <ButtonV3
                variant="text"
                color="blue"
                className={cn(styles.addIndustryButton, styles.mb24)}
                onClick={() => setShowIndustryNameInput(true)}
              >
                {t('step-4.industry-name.add.add-link')}
              </ButtonV3>
            </Tooltip>
          )}
          {showSubIndustry && industryOptions && (
            <Row className={styles.mb24}>
              <Col span={24} className={styles.companyIndustry}>
                <div>
                  <DropdownV3
                    title={t('step-4.sub-industry.title')}
                    label={t('step-4.sub-industry.label')}
                    value={currentSubIndustryId || ''}
                    onChange={(value: string) => {
                      setValue('sub_industry_id', value);
                      setError('sub_industry_id', {});
                    }}
                    options={industryOptions.filter(
                      (industry) => industry.value !== currentIndustryId
                    )}
                    isSearchable={true}
                    searchPlaceholder={t('search-placeholder')}
                    selectedValueLabel={selectedSubIndustry}
                    isValid={!errors.sub_industry_id?.message}
                    labelClassName={styles.fieldLabel}
                    className={styles.CompanyDropDown}
                    triggerButtonClassName={styles.triggerButton}
                  />
                  <CancelButton
                    onRemove={handleSubIndustryDelete}
                    isShow={true}
                    placeholderClassName={styles.closeIcon}
                  />
                </div>
                {errors.sub_industry_id?.message && (
                  <div className={styles.errorMessage}>
                    {errors.sub_industry_id?.message}
                  </div>
                )}
              </Col>
            </Row>
          )}

          {employeesCountRange && (
            <Row className={styles.mb24}>
              <Col span={24}>
                <DropdownV3
                  title={t('step-4.company-size.title')}
                  label={t('step-4.company-size.label')}
                  value={currentEmployeesCount || ''}
                  onChange={(value: string) => {
                    setValue('employees_count', value);
                    setError('employees_count', {});
                  }}
                  options={employeesCountRange}
                  isSearchable={true}
                  searchPlaceholder={t('search-placeholder')}
                  selectedValueLabel={selectedEmployeesCount}
                  isValid={!errors.employees_count?.message}
                  labelClassName={styles.fieldLabel}
                  className={styles.CompanyDropDown}
                  triggerButtonClassName={styles.triggerButton}
                />
                {errors.employees_count?.message && (
                  <div className={styles.errorMessage}>
                    {errors.employees_count?.message}
                  </div>
                )}
              </Col>
            </Row>
          )}
          <Row className={styles.imageUploader}>
            <Col span={24}>
              <div className={styles.fieldLabel}>
                <span> {t('step-4.logo.label')}</span>
              </div>
              <input
                type="file"
                accept="image/*"
                id={`upload-profile-image`}
                hidden
                onChange={uploadImage}
              />
              {logoImage ? (
                <div className={styles.uploadedFile}>
                  <div className={styles.avatarWrapper}>
                    <Image src={logoImage} className={styles.image} />
                  </div>

                  <label
                    htmlFor={`upload-profile-image`}
                    className={styles.changePhoto}
                  >
                    <Icon iconName="edit-v2" />
                    <div>{t('step-4.logo.change')}</div>
                  </label>

                  <Button
                    type="button"
                    color="secondary"
                    className={styles.changePhoto}
                    onClick={() => setValue('logo', '')}
                  >
                    <Icon iconName="trash" />
                    {t('step-4.logo.remove')}
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor={`upload-profile-image`}
                  className={cn(styles.mainButton, {
                    [styles.error]: !!errors.logo?.message,
                  })}
                >
                  {t('step-4.logo.upload')}
                </label>
              )}
              {errors.logo?.message && (
                <div className={styles.errorMessage}>
                  {errors.logo?.message}
                </div>
              )}
            </Col>
          </Row>

          <Row className={styles.mb24}>
            <Col span={24}>
              <InputV2
                label={t('step-4.short-description.label')}
                {...register('short_description', {
                  required: t('step-4.short-description.required'),
                })}
                placeholder={t('step-4.short-description.placeholder')}
                isInvalid={!!errors.short_description}
                labelClassName={styles.fieldLabel}
              />

              {errors.short_description?.message && (
                <div className={styles.errorMessage}>
                  {errors.short_description?.message}
                </div>
              )}
            </Col>
          </Row>
          <Row className={styles.mb24}>
            <Col span={24}>
              <TextArea
                rows={4}
                label={t('step-4.description.label')}
                {...register('description', {
                  required: t('step-4.description.required'),
                })}
                placeholder={t('step-4.description.placeholder')}
                isValid={!errors.description}
                labelClassName={styles.fieldLabel}
                textAreaClassName={cn(styles.textArea, {
                  [styles.error]: errors.description?.message,
                })}
                infoToolTip={
                  <Tooltip
                    content={t('step-4.description.tooltip')}
                    position="top"
                    xAlign="center"
                    color="dark"
                    className={styles.tooltipContainer}
                  >
                    <Icon
                      iconName="icon_info-outline"
                      className={styles.tooltipContainerIcon}
                      size="small"
                    />
                  </Tooltip>
                }
                maxLength={600}
              />
              {errors.description?.message && (
                <div className={styles.errorMessage}>
                  {errors.description?.message}
                </div>
              )}
            </Col>
          </Row>

          {showError && <div className={styles.errorMessage}>{showError}</div>}
          <Footer>
            <ButtonV3
              type="submit"
              isFullWidth
              isLoading={isSubmitting}
              size="xlarge"
            >
              {t('next')}
            </ButtonV3>
          </Footer>
        </Form>
      </div>
    </>
  );
};
